/**
 * Utility functions for the backtest engine
 */
import { HistoricalPrice } from './types.js';

/**
 * Get all unique trading days from historical data, sorted chronologically
 */
export function generateTradingDays(historicalData: HistoricalPrice[]): string[] {
  const tradingDaysSet = new Set<string>();
  
  for (const dataPoint of historicalData) {
    // Extract just the date part (YYYY-MM-DD)
    const datePart = dataPoint.date.split('T')[0];
    tradingDaysSet.add(datePart);
  }
  
  // Convert to array and sort chronologically
  return Array.from(tradingDaysSet).sort();
}

/**
 * Find the next trading day after a given date
 */
export function findNextTradingDay(currentDate: string, tradingDays: string[]): string | null {
  const currentIndex = tradingDays.indexOf(currentDate);
  if (currentIndex === -1 || currentIndex === tradingDays.length - 1) {
    return null; // Date not found or already at the last day
  }
  return tradingDays[currentIndex + 1];
}

/**
 * Group historical data by symbol for quicker lookups
 */
export function groupDataBySymbol(data: HistoricalPrice[]): Record<string, HistoricalPrice[]> {
  const result: Record<string, HistoricalPrice[]> = {};
  
  for (const point of data) {
    if (!result[point.symbol]) {
      result[point.symbol] = [];
    }
    result[point.symbol].push(point);
  }
  
  // Sort each symbol's data chronologically
  for (const symbol in result) {
    result[symbol].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }
  
  return result;
}

/**
 * Get historical data for a specific date range
 */
export function getDataForDateRange(
  data: HistoricalPrice[],
  startDate: string,
  endDate: string
): HistoricalPrice[] {
  return data.filter(d => {
    const date = d.date.split('T')[0];
    return date >= startDate && date <= endDate;
  });
}

/**
 * Calculate simple moving average for a specific period
 */
export function calculateSMA(prices: number[], period: number): number | null {
  if (prices.length < period) return null;
  
  const sum = prices.slice(-period).reduce((total, price) => total + price, 0);
  return sum / period;
}

/**
 * Calculate exponential moving average
 */
export function calculateEMA(prices: number[], period: number, prevEMA?: number): number | null {
  if (prices.length < period) return null;
  
  // If no previous EMA provided, use SMA as first value
  if (prevEMA === undefined) {
    prevEMA = calculateSMA(prices.slice(0, period), period);
    if (prevEMA === null) return null;
    
    // If we only have exactly period values, return the SMA
    if (prices.length === period) return prevEMA;
  }
  
  // Calculate multiplier
  const multiplier = 2 / (period + 1);
  
  // Calculate EMA
  const currentPrice = prices[prices.length - 1];
  return currentPrice * multiplier + prevEMA * (1 - multiplier);
}

/**
 * Calculate Relative Strength Index (RSI)
 */
export function calculateRSI(prices: number[], period: number = 14): number | null {
  if (prices.length <= period) return null;
  
  // Calculate price changes
  const changes: number[] = [];
  for (let i = 1; i < prices.length; i++) {
    changes.push(prices[i] - prices[i - 1]);
  }
  
  // Get gains and losses
  const gains = changes.map(c => c > 0 ? c : 0);
  const losses = changes.map(c => c < 0 ? Math.abs(c) : 0);
  
  // Calculate average gain and loss for the first period
  let avgGain = gains.slice(0, period).reduce((sum, g) => sum + g, 0) / period;
  let avgLoss = losses.slice(0, period).reduce((sum, l) => sum + l, 0) / period;
  
  // Handle edge case - if avgLoss is zero, RSI is 100
  if (avgLoss === 0) return 100;
  
  // Calculate RS (Relative Strength) and RSI
  let rs = avgGain / avgLoss;
  let rsi = 100 - (100 / (1 + rs));
  
  return rsi;
}

/**
 * Calculate Moving Average Convergence Divergence (MACD)
 */
export function calculateMACD(
  prices: number[], 
  fastPeriod: number = 12, 
  slowPeriod: number = 26, 
  signalPeriod: number = 9
): { macd: number | null; signal: number | null; histogram: number | null } {
  if (prices.length < slowPeriod + signalPeriod) {
    return { macd: null, signal: null, histogram: null };
  }
  
  // Calculate EMAs
  let fastEMA = calculateEMA(prices, fastPeriod);
  let slowEMA = calculateEMA(prices, slowPeriod);
  
  if (fastEMA === null || slowEMA === null) {
    return { macd: null, signal: null, histogram: null };
  }
  
  // MACD Line = Fast EMA - Slow EMA
  const macdLine = fastEMA - slowEMA;
  
  // We need more data points to calculate the signal line
  // In a real implementation, you would maintain a history of MACD values
  // For simplicity, we'll use the current MACD value
  const signal = macdLine; // This is a simplification - normally you'd use the history
  
  // MACD Histogram = MACD Line - Signal Line
  const histogram = macdLine - signal;
  
  return { macd: macdLine, signal, histogram };
}

/**
 * Convert symbol history to OHLCV bars with specified timeframe
 */
export function convertToTimeframe(
  data: HistoricalPrice[],
  timeframe: 'minute' | '5minute' | '15minute' | 'hour' | 'day' | 'week'
): HistoricalPrice[] {
  if (timeframe === 'minute') return data; // Already in the base timeframe
  
  const result: HistoricalPrice[] = [];
  const symbol = data[0]?.symbol || '';
  
  // Group data by the specified timeframe
  const groupedData: Record<string, HistoricalPrice[]> = {};
  
  for (const bar of data) {
    let key: string;
    const date = new Date(bar.date);
    
    switch (timeframe) {
      case '5minute':
        const minute5 = Math.floor(date.getMinutes() / 5) * 5;
        key = `${date.getFullYear()}-${padZero(date.getMonth() + 1)}-${padZero(date.getDate())} ${padZero(date.getHours())}:${padZero(minute5)}`;
        break;
      case '15minute':
        const minute15 = Math.floor(date.getMinutes() / 15) * 15;
        key = `${date.getFullYear()}-${padZero(date.getMonth() + 1)}-${padZero(date.getDate())} ${padZero(date.getHours())}:${padZero(minute15)}`;
        break;
      case 'hour':
        key = `${date.getFullYear()}-${padZero(date.getMonth() + 1)}-${padZero(date.getDate())} ${padZero(date.getHours())}:00`;
        break;
      case 'day':
        key = `${date.getFullYear()}-${padZero(date.getMonth() + 1)}-${padZero(date.getDate())}`;
        break;
      case 'week':
        // Get start of week (Sunday)
        const dayOfWeek = date.getDay();
        const diff = date.getDate() - dayOfWeek;
        const startOfWeek = new Date(date);
        startOfWeek.setDate(diff);
        key = `${startOfWeek.getFullYear()}-${padZero(startOfWeek.getMonth() + 1)}-${padZero(startOfWeek.getDate())}`;
        break;
      default:
        key = bar.date;
    }
    
    if (!groupedData[key]) {
      groupedData[key] = [];
    }
    
    groupedData[key].push(bar);
  }
  
  // Create OHLCV bars from each group
  for (const key in groupedData) {
    const bars = groupedData[key];
    
    if (bars.length === 0) continue;
    
    // Sort by time
    bars.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    const open = bars[0].open;
    const close = bars[bars.length - 1].close;
    let high = bars[0].high;
    let low = bars[0].low;
    let volume = 0;
    
    // Find the high and low and sum volume
    for (const bar of bars) {
      high = Math.max(high, bar.high);
      low = Math.min(low, bar.low);
      volume += bar.volume;
    }
    
    result.push({
      date: key,
      open,
      high,
      low,
      close,
      volume,
      symbol
    });
  }
  
  // Sort by date
  return result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

// Helper function to pad zero for date formatting
function padZero(num: number): string {
  return num.toString().padStart(2, '0');
}